import {
  QueryClient,
  MutationCache,
  QueryCache,
  type Query,
} from "@tanstack/react-query";

import {
  getBackendMessage,
  resolveBackendErrorMessageId,
} from "../common/hooks/useDisplayErrorMessage";

import { publishMutationError } from "./mutation-error-bus";
import { logger } from "./services/SentryLogger.service";

const staleTime = {
  Infinity,
  oneMinute: 60_000,
  oneDay: 24 * 60 * 60 * 1000,
};

// oxlint-disable-next-line import/no-mutable-exports
let queryClient: QueryClient;

const queryCache = new QueryCache({
  onError: (error) => {
    logger.error(error);
  },
});

const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    logger.error(error);
    if (mutation.meta?.silent) return;

    const backendErrorMessageId = resolveBackendErrorMessageId(error);
    const errorMessageId =
      backendErrorMessageId ?? mutation.meta?.errorMessageId;

    if (errorMessageId) {
      publishMutationError({
        errorMessageId,
        errorMessageValues: {
          message: getBackendMessage(error),
          ...mutation.meta?.errorMessageValues,
        },
      });
    } else {
      publishMutationError({});
    }
  },
  onSuccess: async (_data, _variables, _context, mutation) => {
    // Skip if mutation opted out via meta.skipInvalidation
    if (mutation.meta?.skipInvalidation) {
      return;
    }

    if (mutation.meta?.awaits === "all") {
      await queryClient.invalidateQueries({ predicate: isInvalidatable });
      return;
    }

    // Invalidate all queries by default
    queryClient.invalidateQueries({ predicate: isInvalidatable });

    // If meta.awaits specified, refetch those queries and wait
    if (mutation.meta?.awaits && Array.isArray(mutation.meta.awaits)) {
      const promises = mutation.meta.awaits.map((queryKey: unknown[]) =>
        queryClient.invalidateQueries({ queryKey }),
      );
      return Promise.all(promises);
    }
  },
});

queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: staleTime.oneMinute,
    },
  },
});

function isInvalidatable(query: Query): boolean {
  return !query.meta?.skipInvalidation;
}

export { queryClient, staleTime };
